import { useParams } from "react-router";

import { ErrorState, LoadingRows } from "@/components/data-state";
import { useAsync } from "@/hooks/use-async";
import { useApiKey } from "@/hooks/use-auth";
import { publicApi } from "@/lib/api";
import { LegacyChallengePage } from "@/legacy/legacy-challenge";
import { SimChallengePage } from "@/routes/challenge/sim-challenge";

/**
 * Fetch the challenge, then hand it to the page for its mode.
 *
 * Only the fetch and its failures live here. The two modes share a URL because a student has
 * one link to a challenge, but they share very little else, and this used to be one component
 * with five `simulator === "server"` branches in it.
 */
export function ChallengeDetailRoute() {
  const { id = "" } = useParams();
  const apiKey = useApiKey();

  // Keyed on the api key as well as the id: an `enrolled` challenge is a 401 until the student
  // signs in, and the page has to refetch when they do rather than stay on the error it got
  // while anonymous.
  const detail = useAsync(() => publicApi.challenge(id), [id, apiKey]);

  if (detail.error) {
    // The server distinguishes "no key sent", "key refused", "wrong course", and "no such
    // challenge", so show what it said rather than flattening all four into
    // "Challenge not available" — which, for someone who only needed to sign in, points at the
    // one thing that is not wrong.
    return <ErrorState error={detail.error} onRetry={detail.refetch} />;
  }
  if (!detail.data) return <LoadingRows rows={3} />;

  return detail.data.simulator === "server" ? (
    <SimChallengePage challenge={detail.data} />
  ) : (
    <LegacyChallengePage challenge={detail.data} />
  );
}
